import { useEffect, useMemo, useState } from 'react';
import {
  useGetPriceProcessingDataQuery,
  useGetPriceProcessingFiltersQuery,
  useSavePriceChangesMutation
} from '@/services';
import { PriceActionFilterKey, PriceActionFilters } from '@/types/common.types';
import { getActiveFilters, getFiltersAfterRemove, getUpdatedFilters } from '@/pages/utils';
import { PriceItem, SavePriceChangesRequest, PriceProcessingData } from '@/types/price-processing.types';

import { formatIntegrationMessage } from '../utils/integrationUtils';
import { getErrorMessage } from '../pages/upload/upload.utils';
import {
  getDefaultDateTime,
  normalizeForComparison,
  normalizeOptionList,
  stationCellKey,
  stationRowKey,
  withAllOption,
  defaultNotifications,
  updateDataDate,
  getOriginalItem,
  isMatchingStationItem,
  getOriginalStationValues
} from '../pages';
import { useSnackbar } from './useSnackbar';

export const usePriceProcessing = () => {
  const { setSnackbarMessage } = useSnackbar();
  const [filters, setFilters] = useState<PriceActionFilters>({});
  const [globalPriceData, setGlobalPriceData] = useState<PriceItem[]>([]);
  const [stationPriceData, setStationPriceData] = useState<PriceItem[]>([]);
  const [originalGlobalPriceData, setOriginalGlobalPriceData] = useState<PriceItem[]>([]);
  const [originalStationPriceData, setOriginalStationPriceData] = useState<PriceItem[]>([]);

  const [changedCells, setChangedCells] = useState(new Set<string>());
  const [effectiveDateTime, setEffectiveDateTime] = useState('');
  const [notificationDateTime, setNotificationDateTime] = useState('');
  const [resetKey, setResetKey] = useState('');

  const [saveConfirmDialogOpen, setSaveConfirmDialogOpen] = useState(false);
  const [createOrder, setCreateOrder] = useState(true);
  const [notifications, setNotifications] = useState<typeof defaultNotifications>(defaultNotifications);

  const {
    data: filtersData,
    isLoading: isFiltersLoading,
    isFetching: isFiltersFetching,
    isError: isFiltersError
  } = useGetPriceProcessingFiltersQuery(filters, { refetchOnMountOrArgChange: true });

  const shouldLoadPriceData = Boolean(filters.regional_office && filters.province);

  const {
    data,
    isLoading: isPriceProcessingDataLoading,
    isFetching: isPriceProcessingDataFetching,
    refetch: refetchPriceProcessingData
  } = useGetPriceProcessingDataQuery(filters, {
    skip: !shouldLoadPriceData,
    refetchOnMountOrArgChange: true
  });
  const [savePriceChanges, { isLoading: isSavePriceChangesLoading }] = useSavePriceChangesMutation();

  useEffect(() => {
    initializeDateTime();
  }, []);

  useEffect(() => {
    if (isFiltersError) {
      setSnackbarMessage('Помилка завантаження даних', 'error');
    }
  }, [isFiltersError, setSnackbarMessage]);

  const priceProcessingData = useMemo(() => {
    if (!shouldLoadPriceData) return null;
    return data || null;
  }, [shouldLoadPriceData, data]);

  useEffect(() => {
    if (!priceProcessingData || !shouldLoadPriceData || isPriceProcessingDataFetching) return;
    applyPriceProcessingData(priceProcessingData);
  }, [priceProcessingData, shouldLoadPriceData, isPriceProcessingDataFetching]);

  const isLoading = isPriceProcessingDataLoading || isPriceProcessingDataFetching || isSavePriceChangesLoading || isFiltersLoading;

  const regionalOffices = useMemo(
    () => normalizeOptionList(filtersData?.regional_offices),
    [filtersData?.regional_offices]
  );
  const provinces = useMemo(() => withAllOption(normalizeOptionList(filtersData?.provinces)), [filtersData?.provinces]);
  const brands = useMemo(() => withAllOption(normalizeOptionList(filtersData?.brands)), [filtersData?.brands]);

  const fuelTypes: string[] = filtersData?.fuel_types?.map((ft: any) => ft.fuel_type).filter(Boolean) || [];

  const activeFilters = useMemo(() => getActiveFilters(filters), [filters]);

  const initializeDateTime = () => {
    const defaultDateTime = getDefaultDateTime();
    setNotificationDateTime(defaultDateTime);
    setEffectiveDateTime(defaultDateTime);
    setResetKey(defaultDateTime);
  };

  const applyPriceProcessingData = (data: PriceProcessingData) => {
    const defaultDateTime = getDefaultDateTime();
    const processedGlobalData = updateDataDate(data.global, defaultDateTime);
    const processedStationData = updateDataDate(data.stations, defaultDateTime);
    setGlobalPriceData(processedGlobalData);
    setStationPriceData(processedStationData);
    setOriginalGlobalPriceData(JSON.parse(JSON.stringify(processedGlobalData)));
    setOriginalStationPriceData(JSON.parse(JSON.stringify(processedStationData)));
  };

  const isCellChanged = (item: PriceItem, fuel: string): boolean => changedCells.has(stationCellKey(item, fuel));

  const hasOkkoChanges = () => {
    return [...globalPriceData, ...stationPriceData].some(item => {
      if (!item.isOkko) return false;
      const rowKey = stationRowKey(item);
      return [...changedCells].some(cellKey => cellKey.startsWith(rowKey) && !cellKey.endsWith('-date'));
    });
  };

  const hasPriceChanges = [...changedCells].some(cellKey => !cellKey.endsWith('-date'));
  const hasChanges = changedCells.size > 0;

  const clearData = () => {
    setGlobalPriceData([]);
    setStationPriceData([]);
    setOriginalGlobalPriceData([]);
    setOriginalStationPriceData([]);
    setChangedCells(new Set());
  };

  const onFilterChange = (filterKey: PriceActionFilterKey, value: string) => {
    clearData();
    const newFilters = getUpdatedFilters(filters, filterKey, value, provinces, brands);
    setFilters(newFilters);
  };

  const removeActiveFilter = (key: PriceActionFilterKey, value?: string) => {
    const newFilters = getFiltersAfterRemove(filters, key, value);
    setFilters(newFilters);
    clearData();
  };

  const onCreateOrderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setCreateOrder(checked);
    if (checked) {
      setNotifications({ sendSMS: true, sendMail: true, send1C: true, sendOkkoCore: true });
    } else {
      setNotifications({ sendSMS: false, sendMail: false, send1C: false, sendOkkoCore: false });
    }
  };

  const onNotificationChange = (newSendSMS?: boolean, newSendMail?: boolean) => {
    const sms = newSendSMS ?? notifications.sendSMS;
    const mail = newSendMail ?? notifications.sendMail;
    if (!sms && !mail) {
      setNotificationDateTime(getDefaultDateTime());
    }
  };

  const onPriceChange = (item: PriceItem, fuel: string, value: string) => {
    const cellKey = stationCellKey(item, fuel);
    const isGlobalItem = globalPriceData.includes(item);
    const originalData = isGlobalItem ? originalGlobalPriceData : originalStationPriceData;
    const originalItem = getOriginalItem(item, originalData);

    const originalValue = originalItem?.prices?.[fuel];
    const normalizedValue = normalizeForComparison(value);
    const normalizedOriginal = normalizeForComparison(originalValue);
    const matchesOriginal = normalizedValue === normalizedOriginal;

    const newChangedCells = new Set(changedCells);

    if (isGlobalItem) {
      if (matchesOriginal) {
        newChangedCells.delete(cellKey);
      } else {
        newChangedCells.add(cellKey);
      }

      // Update all matching station cells
      stationPriceData.forEach(stationItem => {
        if (isMatchingStationItem(stationItem, item)) {
          const stationItemCellKey = stationCellKey(stationItem, fuel);
          const sRowKey = stationRowKey(stationItem);

          // Check if station value matches its original
          const origStation = getOriginalStationValues(originalStationPriceData, stationItem);
          const origStationValue = origStation?.prices?.[fuel];
          const normalizedStationOriginal = normalizeForComparison(origStationValue);

          if (normalizedValue === normalizedStationOriginal) {
            newChangedCells.delete(stationItemCellKey);
            // Remove date highlight if no other price changes remain for this station row
            const hasOtherChanges = [...newChangedCells].some(
              c => c.startsWith(sRowKey) && !c.endsWith('-date') && c !== stationItemCellKey
            );
            if (!hasOtherChanges) {
              newChangedCells.delete(`${sRowKey}-date`);
            }
          } else {
            newChangedCells.add(stationItemCellKey);
            // Add date highlight for this station row
            newChangedCells.add(`${sRowKey}-date`);
          }
        }
      });

      // Check if there are any price changes left for this brand/region/province
      const itemRowKey = stationRowKey(item);
      const hasAnyPriceChanges = [...newChangedCells].some(cell => {
        if (cell.endsWith('-date')) return false;
        return stationPriceData.some(
          s =>
            s.brand === item.brand &&
            s.region === item.region &&
            s.province === item.province &&
            cell.startsWith(stationRowKey(s))
        );
      });

      // Remove global date cell if no price changes remain
      const dateCellKey = `${itemRowKey}-date`;
      if (!hasAnyPriceChanges) {
        newChangedCells.delete(dateCellKey);
      } else {
        newChangedCells.add(dateCellKey);
      }

      const updatedGlobalData = globalPriceData.map(globalItem => {
        if (globalItem === item) {
          return {
            ...globalItem,
            prices: { ...globalItem.prices, [fuel]: value === '' ? null : value },
            changeDate: effectiveDateTime
          };
        }
        return globalItem;
      });

      const updatedStationData = stationPriceData.map(stationItem => {
        if (isMatchingStationItem(stationItem, item)) {
          return {
            ...stationItem,
            prices: { ...stationItem.prices, [fuel]: value === '' ? null : value },
            changeDate: effectiveDateTime
          };
        }
        return stationItem;
      });

      setGlobalPriceData(updatedGlobalData);
      setStationPriceData(updatedStationData);
    } else {
      if (matchesOriginal) {
        newChangedCells.delete(cellKey);
      } else {
        newChangedCells.add(cellKey);
      }

      const updatedStationData = stationPriceData.map(stationItem => {
        if (stationItem === item || stationItem.station_unicode === item.station_unicode) {
          return {
            ...stationItem,
            prices: { ...stationItem.prices, [fuel]: value === '' ? null : value },
            changeDate: effectiveDateTime
          };
        }
        return stationItem;
      });

      // Add/remove date cell highlight for this station row only
      const rowKey = stationRowKey(item);
      const stationDateCellKey = `${rowKey}-date`;
      if (!matchesOriginal) {
        newChangedCells.add(stationDateCellKey);
      } else {
        const hasOtherChanges = [...newChangedCells].some(c => c.startsWith(rowKey) && !c.endsWith('-date'));
        if (!hasOtherChanges) {
          newChangedCells.delete(stationDateCellKey);
        }
      }

      setStationPriceData(updatedStationData);
    }

    setChangedCells(newChangedCells);
  };

  const handlePriceInput = (e: React.ChangeEvent<HTMLInputElement>, item: PriceItem, fuel: string) => {
    const input = e.currentTarget;
    const value = input.value;

    if (value === '1') {
      onPriceChange(item, fuel, value);
      return;
    }

    const cleanValue = value.replace(/[^0-9,]/g, '');
    const parts = cleanValue.split(',');
    const formattedValue = parts.length > 2 ? parts[0] + ',' + parts.slice(1).join('') : cleanValue;
    const finalValue = formattedValue.includes(',')
      ? formattedValue.split(',')[0] + ',' + formattedValue.split(',')[1].substring(0, 2)
      : formattedValue;

    // Reject zero — treat as empty
    const numericCheck = parseFloat(finalValue.replace(',', '.'));
    if (!isNaN(numericCheck) && numericCheck === 0 && !/^0,/.test(finalValue)) {
      input.value = '';
      onPriceChange(item, fuel, '');
      return;
    }

    if (finalValue !== value) {
      input.value = finalValue;
    }

    onPriceChange(item, fuel, finalValue);
  };

  const onDateChange = (_: PriceItem, value: string | null) => {
    const resolvedValue = value || getDefaultDateTime();
    setEffectiveDateTime(resolvedValue);

    const newChangedCells = new Set(changedCells);

    // Update date for ALL station rows with price changes
    setStationPriceData(
      stationPriceData.map(s => {
        const sRowKey = stationRowKey(s);
        const hasChanges = [...newChangedCells].some(c => c.startsWith(sRowKey) && !c.endsWith('-date'));
        if (hasChanges) {
          newChangedCells.add(`${sRowKey}-date`);
          return { ...s, changeDate: resolvedValue };
        }
        return s;
      })
    );

    // Update global changeDate silently (no highlight)
    setGlobalPriceData(
      globalPriceData.map(g => {
        const gRowKey = stationRowKey(g);
        const hasChanges = [...newChangedCells].some(c => c.startsWith(gRowKey) && !c.endsWith('-date'));
        return hasChanges ? { ...g, changeDate: resolvedValue } : g;
      })
    );

    setChangedCells(newChangedCells);
  };

  const onSave = (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    // Pre-set createOrder and notifications based on whether OKKO changes are present
    if (hasOkkoChanges()) {
      setCreateOrder(true);
      setNotifications({ sendSMS: true, sendMail: true, send1C: true, sendOkkoCore: true });
    } else {
      setCreateOrder(false);
      setNotifications({ ...defaultNotifications });
    }
    setSaveConfirmDialogOpen(true);
  };

  const onConfirmSave = async () => {
    try {
      const updates = stationPriceData
        .filter(item => {
          const rowKey = stationRowKey(item);
          return [...changedCells].some(cellKey => cellKey.startsWith(rowKey) && !cellKey.endsWith('-date'));
        })
        .map(item => {
          const fuel_prices: { [key: string]: number | null } = {};

          Object.keys(item.prices).forEach(fuel => {
            const cellKey = stationCellKey(item, fuel);
            if (changedCells.has(cellKey)) {
              const price = item.prices[fuel];
              if (price === '1') {
                fuel_prices[fuel] = 1;
              } else if (typeof price === 'string' && price !== '') {
                fuel_prices[fuel] = parseFloat(price.replace(',', '.'));
              } else if (typeof price === 'number') {
                fuel_prices[fuel] = price;
              } else {
                fuel_prices[fuel] = null;
              }
            }
          });

          return {
            fuel_prices,
            price_change_date: effectiveDateTime ? new Date(effectiveDateTime).toISOString() : new Date().toISOString(),
            station_unicode: item.station_unicode,
            brand_id: item.brand_id
          };
        })
        .filter(update => Object.keys(update.fuel_prices).length > 0);

      if (updates.length === 0) {
        setSnackbarMessage('Немає змін для збереження', 'error');
        setSaveConfirmDialogOpen(false);
        return;
      }

      const saveData: SavePriceChangesRequest = {
        updates,
        notifications: {
          createOrder,
          sendSMS: createOrder ? notifications.sendSMS : false,
          sendMail: createOrder ? notifications.sendMail : false,
          send1C: createOrder ? notifications.send1C : false,
          sendOkkoCore: createOrder ? notifications.sendOkkoCore : false
        },
        notification_datetime: notificationDateTime
          ? new Date(notificationDateTime).toISOString()
          : new Date().toISOString()
      };

      const response = await savePriceChanges(saveData).unwrap();
      setChangedCells(new Set());
      setSaveConfirmDialogOpen(false);
      resetDialogState();

      await new Promise(resolve => setTimeout(resolve, 1000));

      const refetchResult = await refetchPriceProcessingData();
      if ('data' in refetchResult && refetchResult.data) {
        applyPriceProcessingData(refetchResult.data);
      }

      const formattedMessage = formatIntegrationMessage(response.message, response.scheduledNotifications);
      setSnackbarMessage(formattedMessage, 'success');
    } catch (error: any) {
      const errorData = error?.data ?? error?.response?.data ?? null;
      const status = error?.status ?? error?.response?.status;

      if (status === 400 && errorData?.validation_errors) {
        let errorMessage = errorData.error || 'Помилка валідації';
        const lines = errorData.validation_errors
          .map((e: any, i: number) => `${i + 1}. Станція ${e.station_code}, ${e.fuel_type}: ${e.error}`)
          .join('<br>');
        errorMessage += `<br><br>${lines}`;
        setSnackbarMessage(errorMessage, 'error', 15000, true);
      } else {
        setSnackbarMessage(getErrorMessage(error), 'error', 15000, true);
      }
      setSaveConfirmDialogOpen(false);
    }
  };

  const onResetChanges = (event?: React.FormEvent) => {
    if (event) {
      event.preventDefault();
    }
    const defaultDateTime = getDefaultDateTime();
    setGlobalPriceData(updateDataDate(originalGlobalPriceData, defaultDateTime));
    setStationPriceData(updateDataDate(originalStationPriceData, defaultDateTime));

    setChangedCells(new Set());
    setEffectiveDateTime(defaultDateTime);
    setResetKey(defaultDateTime);
    setSnackbarMessage('Зміни скинуто', 'success');
  };

  const resetDialogState = () => {
    setCreateOrder(false);
    setNotifications({ ...defaultNotifications });
    setEffectiveDateTime(getDefaultDateTime());
  };

  return {
    regionalOffices,
    provinces,
    brands,
    fuelTypes,
    filters,
    activeFilters,
    globalPriceData,
    stationPriceData,
    hasChanges,
    isFiltersLoading,
    isFiltersFetching,

    hasPriceChanges,
    saveConfirmDialogOpen,
    createOrder,
    notifications,
    notificationDateTime,
    isLoading,
    resetKey: resetKey,

    isCellChanged,
    hasOkkoChanges,
    setNotificationDateTime,
    setSaveConfirmDialogOpen,
    setNotifications,
    resetDialogState,
    onFilterChange,
    removeActiveFilter,
    onCreateOrderChange,
    onNotificationChange,
    handlePriceInput,
    onSave,
    onConfirmSave,
    onResetChanges,
    onDateChange
  };
};
